/**
 * 食味評価(フィニッシュ評価)の集計と表示ビュー。
 *
 * 2026-08-19 熊谷さんMTGの決定(通信簿形式・第三者評価)。味はカメラに映らない
 * 情報なので人(コーチ)が入れる。VLMに推測させない(#7 #8 #9)の適用対象であり、
 * このモジュールは入力値の集計しかしない。
 *
 * **1..5 → 0..100 の換算式はここだけが持つ(#21 #40)。** Functions 側の
 * functions/tasting-lib.mjs は同一実装の複製(web は Vite/TS、functions は素の ESM で
 * モジュールを共有できない)。両者のズレは tests/tasting-parity.test.ts が検出する。
 *
 * 工程スコア(evaluations.score)とは別の数字。合算した「総合点」は作らない。
 */

/** テナント設定 tenants/{t}.tastingAxes。軸の中身は欣葉側の意思決定事項 */
export interface TastingAxisDef {
  axisId: string;
  label: string;
  description: string | null;
  /** 既定は 1(等重み)。合計での正規化は tastingScore が行う */
  weight: number;
}

export interface TastingAxes {
  version: number;
  axes: TastingAxisDef[];
}

/** evaluations/{evalId}/tasting/{evaluatorUid} の1軸分の入力 */
export interface TastingEntryAxis {
  axisId: string;
  /** 1..5 の整数 */
  score: number;
  comment: string | null;
}

export interface TastingEntry {
  axes: TastingEntryAxis[];
  comment: string | null;
  /** 入力時点の tastingAxes.version(軸改訂後も過去の入力を読めるように) */
  axesVersion: number;
  evaluatorUid: string;
  evaluatorName: string;
  evaluatorRole: "admin" | "chef" | "supervisor";
  createdAt: { toDate(): Date } | null;
  updatedAt: { toDate(): Date } | null;
}

/** 味の表示・入力を出すか。軸が未設定のテナントでは食味UIを一切出さない(機能フラグ) */
export function tastingEnabled(config: TastingAxes | null | undefined): config is TastingAxes {
  return !!config && Array.isArray(config.axes) && config.axes.length > 0;
}

/** フォームの入力バリデーション。1..5 の整数だけを通す */
export function isValidAxisScore(n: unknown): n is number {
  return typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 5;
}

/**
 * 軸ごとの 1..5 を重み付き平均し、0..100 の整数へ。空配列は null(未評価)。
 *
 * 換算は 平均 × 20(5段階の4 → 80点。「工程は100点、味は80点」の通信簿表現と揃える)。
 * 最低評価(全軸1)は 20点であって 0点ではない。0..100 は値域であり、
 * 0点は「未評価」ではなく作れない値(未評価は null で区別する)。
 *
 * 範囲外・非整数は入力バリデーション(isValidAxisScore)で弾くのが前提だが、
 * 万一混ざった場合は誤った点を出すより null(未評価扱い)を返す。
 */
export function tastingScore(axes: { score: number; weight: number }[]): number | null {
  if (!Array.isArray(axes) || axes.length === 0) return null;
  let sum = 0;
  let weightSum = 0;
  for (const a of axes) {
    if (!isValidAxisScore(a.score)) return null;
    if (!Number.isFinite(a.weight) || a.weight <= 0) return null;
    sum += a.score * a.weight;
    weightSum += a.weight;
  }
  return Math.round((sum / weightSum) * 20);
}

/** 入力1件の総合点。重みは現行の軸設定から引き、無い軸(改訂で消えた等)は等重み1 */
export function entryScore(entry: Pick<TastingEntry, "axes">, config: TastingAxes | null): number | null {
  const defs = new Map((config?.axes ?? []).map((a) => [a.axisId, a]));
  return tastingScore(
    entry.axes.map((a) => ({ score: a.score, weight: defs.get(a.axisId)?.weight ?? 1 })),
  );
}

export interface TastingView {
  total: number | null;
  axes: { axisId: string; label: string; score: number; comment: string | null }[];
  comment: string | null;
  evaluatorName: string;
  evaluatorRole: TastingEntry["evaluatorRole"];
  at: Date | null;
}

/**
 * TastingEntry + TastingAxes から表示用ビューを組む。
 * axesVersion の不一致などで現行の軸設定に無い axisId は、当時の axisId をそのまま
 * ラベルとして出す(隠したり捨てたりしない。過去の入力が読める状態を保つ)。
 */
export function toTastingView(entry: TastingEntry, config: TastingAxes | null): TastingView {
  const defs = new Map((config?.axes ?? []).map((a) => [a.axisId, a]));
  return {
    total: entryScore(entry, config),
    axes: entry.axes.map((a) => ({
      axisId: a.axisId,
      label: defs.get(a.axisId)?.label ?? a.axisId,
      score: a.score,
      comment: a.comment,
    })),
    comment: entry.comment,
    evaluatorName: entry.evaluatorName,
    evaluatorRole: entry.evaluatorRole,
    at: entry.updatedAt?.toDate() ?? null,
  };
}

/**
 * 複数評価者の入力を「代表を先頭に」並べる。代表 = 最新 updatedAt(Functions の
 * 非正規化と同じ規則。functions/tasting-lib.mjs と揃えること)。
 */
export function sortTastingEntries<T extends { updatedAt: { toDate(): Date } | null }>(entries: T[]): T[] {
  return [...entries].sort(
    (a, b) => (b.updatedAt?.toDate().getTime() ?? 0) - (a.updatedAt?.toDate().getTime() ?? 0),
  );
}
